// src/data/mock/partners.ts

/**
 * Partners Data Structure
 * 
 * Information about Champions Academy partners
 * including global clubs, local organizations, and sponsors
 */

export type PartnerType = 'global' | 'local' | 'sponsor' | 'media';
export type PartnerCategory = 'football' | 'basketball' | 'general' | 'technology' | 'financial' | 'media' | 'government';

export interface Partner {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  logo: string;
  type: PartnerType;
  category?: PartnerCategory;
  website?: string;
  description: {
    en: string;
    ar: string;
  };
  partnershipDetails?: {
    en: string;
    ar: string;
  };
  since?: number; // Year partnership started
  featured?: boolean;
  active?: boolean;
  country?: string;
  achievements?: {
    en: string[];
    ar: string[];
  };
}

/**
 * Global Partners Database
 */
export const globalPartners: Partner[] = [
  {
    id: 'real-madrid',
    name: {
      en: 'Real Madrid Foundation',
      ar: 'مؤسسة ريال مدريد'
    },
    logo: '/assets/partners/s-blob-v1-IMAGE-1w2ET9wdSlw.png',
    type: 'global',
    category: 'football',
    website: 'https://www.realmadrid.com/en/foundation',
    description: {
      en: 'Partnership with Real Madrid Foundation for football development and youth training programs, bringing world-class coaching methodology to Gaza.',
      ar: 'شراكة مع مؤسسة ريال مدريد لتطوير كرة القدم وبرامج تدريب الشباب، مع جلب منهجية تدريب عالمية إلى غزة.'
    },
    partnershipDetails: {
      en: 'Technical training programs, coach development, youth academies',
      ar: 'برامج تدريب فنية، تطوير المدربين، أكاديميات الناشئين'
    },
    since: 2018,
    country: 'Spain',
    featured: true,
    active: true,
    achievements: {
      en: [
        'Trained 500+ young footballers',
        '15 coaches certified',
        '3 international tournaments'
      ],
      ar: [
        'تدريب 500+ لاعب شاب',
        'اعتماد 15 مدرباً',
        '3 بطولات دولية'
      ]
    }
  },
  {
    id: 'barcelona',
    name: {
      en: 'FC Barcelona',
      ar: 'نادي برشلونة'
    },
    logo: '/assets/partners/s-blob-v1-IMAGE-6iuThUTgZKg.png',
    type: 'global',
    category: 'football',
    website: 'https://www.fcbarcelona.com',
    description: {
      en: 'Collaboration with FC Barcelona for coaching methodology, tactical training, and player development following the famous La Masia philosophy.',
      ar: 'تعاون مع نادي برشلونة في منهجية التدريب والتدريب التكتيكي وتطوير اللاعبين وفق فلسفة لا ماسيا الشهيرة.'
    },
    partnershipDetails: {
      en: 'La Masia methodology, technical workshops, youth exchanges',
      ar: 'منهجية لا ماسيا، ورش عمل فنية، تبادل الشباب'
    },
    since: 2019,
    country: 'Spain',
    featured: true,
    active: true,
    achievements: {
      en: [
        'Barcelona methodology implemented',
        '300+ players trained',
        'Annual coaching seminars'
      ],
      ar: [
        'تطبيق منهجية برشلونة',
        'تدريب 300+ لاعب',
        'ندوات تدريب سنوية'
      ]
    }
  },
  {
    id: 'metrica-sports',
    name: {
      en: 'Metrica Sports',
      ar: 'ميتريكا سبورتس'
    },
    logo: '/assets/partners/s-blob-v1-IMAGE-7rbMBLqn8FA.png',
    type: 'global',
    category: 'technology',
    website: 'https://metrica-sports.com',
    description: {
      en: 'Technology partnership providing advanced sports analytics, AI-powered performance tracking, and data-driven training insights.',
      ar: 'شراكة تقنية توفر تحليلات رياضية متقدمة، تتبع أداء بالذكاء الاصطناعي، ورؤى تدريب قائمة على البيانات.'
    },
    partnershipDetails: {
      en: 'Performance analytics platform, player tracking systems, AI insights',
      ar: 'منصة تحليل الأداء، أنظمة تتبع اللاعبين، رؤى الذكاء الاصطناعي'
    },
    since: 2021,
    country: 'International',
    featured: true,
    active: true
  },
  {
    id: 'uefa',
    name: {
      en: 'UEFA Foundation',
      ar: 'مؤسسة اليويفا'
    },
    logo: '/assets/partners/s-blob-v1-IMAGE-1w2ET9wdSlw.png', // Update with actual logo
    type: 'global',
    category: 'football',
    description: {
      en: 'Support from UEFA Foundation for Children for football development and social impact programs.',
      ar: 'دعم من مؤسسة اليويفا للأطفال لتطوير كرة القدم وبرامج التأثير الاجتماعي.'
    },
    since: 2020,
    country: 'Switzerland',
    active: true
  },
  {
    id: 'fiba',
    name: {
      en: 'FIBA',
      ar: 'الاتحاد الدولي لكرة السلة'
    },
    logo: '/assets/partners/s-blob-v1-IMAGE-6iuThUTgZKg.png', // Update with actual logo
    type: 'global',
    category: 'basketball',
    description: {
      en: 'Partnership with FIBA for basketball development, coaching certification, and youth programs.',
      ar: 'شراكة مع FIBA لتطوير كرة السلة واعتماد المدربين وبرامج الشباب.'
    },
    since: 2020,
    country: 'Switzerland',
    active: true
  }
];

/**
 * Local Partners Database
 */
export const localPartners: Partner[] = [
  {
    id: 'unrwa',
    name: {
      en: 'UNRWA',
      ar: 'الأونروا'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-1OoynJdkVaU.png',
    type: 'local',
    category: 'government',
    website: 'https://www.unrwa.org',
    description: {
      en: 'Strategic partnership with UNRWA supporting youth programs, community sports facilities, and educational opportunities through sports.',
      ar: 'شراكة استراتيجية مع الأونروا لدعم برامج الشباب والمرافق الرياضية المجتمعية والفرص التعليمية من خلال الرياضة.'
    },
    partnershipDetails: {
      en: 'Community programs, facility support, youth scholarships',
      ar: 'البرامج المجتمعية، دعم المرافق، منح الشباب'
    },
    since: 2015,
    country: 'Palestine',
    featured: true,
    active: true,
    achievements: {
      en: [
        '2000+ youth beneficiaries',
        '5 community centers supported',
        'Annual sports festivals'
      ],
      ar: [
        '2000+ مستفيد من الشباب',
        'دعم 5 مراكز مجتمعية',
        'مهرجانات رياضية سنوية'
      ]
    }
  },
  {
    id: 'paltel',
    name: {
      en: 'Paltel Group',
      ar: 'مجموعة بالتل'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-5twyxSuD2vs.png',
    type: 'local',
    category: 'technology',
    website: 'https://www.paltel.ps',
    description: {
      en: 'Technology partnership with Paltel Group providing digital infrastructure, connectivity solutions, and tech support for academy operations.',
      ar: 'شراكة تقنية مع مجموعة بالتل توفر البنية التحتية الرقمية وحلول الاتصال والدعم التقني لعمليات الأكاديمية.'
    },
    partnershipDetails: {
      en: 'Internet connectivity, digital platforms, tech infrastructure',
      ar: 'اتصال الإنترنت، المنصات الرقمية، البنية التحتية التقنية'
    },
    since: 2016,
    country: 'Palestine',
    featured: true,
    active: true
  },
  {
    id: 'jawwal',
    name: {
      en: 'Jawwal',
      ar: 'جوال'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-d5n4kzEr4Ao.png',
    type: 'local',
    category: 'technology',
    website: 'https://www.jawwal.ps',
    description: {
      en: 'Partnership with Jawwal supporting mobile technology integration, digital engagement, and youth communication platforms.',
      ar: 'شراكة مع جوال لدعم تكامل تكنولوجيا الهاتف المحمول والمشاركة الرقمية ومنصات التواصل للشباب.'
    },
    since: 2017,
    country: 'Palestine',
    active: true
  },
  {
    id: 'bank-of-palestine',
    name: {
      en: 'Bank of Palestine',
      ar: 'بنك فلسطين'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-fr-RpkbiWGA.png',
    type: 'local',
    category: 'financial',
    website: 'https://www.bankofpalestine.com',
    description: {
      en: 'Financial partnership with Bank of Palestine supporting academy operations, scholarships, and community development programs.',
      ar: 'شراكة مالية مع بنك فلسطين لدعم عمليات الأكاديمية والمنح الدراسية وبرامج التنمية المجتمعية.'
    },
    partnershipDetails: {
      en: 'Financial support, scholarship funds, community programs',
      ar: 'الدعم المالي، صناديق المنح، البرامج المجتمعية'
    },
    since: 2016,
    country: 'Palestine',
    featured: true,
    active: true
  },
  {
    id: 'pepsi',
    name: {
      en: 'Pepsi Palestine',
      ar: 'بيبسي فلسطين'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-IkBTE2ytQUA.png',
    type: 'sponsor',
    category: 'general',
    description: {
      en: 'Sponsorship partnership with Pepsi supporting events, tournaments, and youth sports activities across all academy programs.',
      ar: 'شراكة رعاية مع بيبسي لدعم الفعاليات والبطولات والأنشطة الرياضية للشباب في جميع برامج الأكاديمية.'
    },
    since: 2018,
    country: 'Palestine',
    active: true
  },
  {
    id: 'local-partner-1',
    name: {
      en: 'Palestinian Football Association',
      ar: 'الاتحاد الفلسطيني لكرة القدم'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-LNR1f2H87qw.png',
    type: 'local',
    category: 'football',
    description: {
      en: 'Official partnership with Palestinian Football Association for youth development and national team pathways.',
      ar: 'شراكة رسمية مع الاتحاد الفلسطيني لكرة القدم لتطوير الشباب ومسارات المنتخب الوطني.'
    },
    since: 2015,
    country: 'Palestine',
    active: true
  },
  {
    id: 'local-partner-2',
    name: {
      en: 'Gaza Sports Authority',
      ar: 'الهيئة العامة للرياضة بغزة'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-LORE-eG6O74.png',
    type: 'local',
    category: 'government',
    description: {
      en: 'Governmental partnership supporting sports infrastructure, licensing, and regulatory compliance.',
      ar: 'شراكة حكومية لدعم البنية التحتية الرياضية والترخيص والامتثال التنظيمي.'
    },
    since: 2015,
    country: 'Palestine',
    active: true
  },
  {
    id: 'local-partner-3',
    name: {
      en: 'Palestine Olympic Committee',
      ar: 'اللجنة الأولمبية الفلسطينية'
    },
    logo: '/assets/partners/local/s-blob-v1-IMAGE-Lv_cOKE8mHs.png',
    type: 'local',
    category: 'general',
    description: {
      en: 'Partnership with Palestine Olympic Committee for elite athlete development and Olympic pathways.',
      ar: 'شراكة مع اللجنة الأولمبية الفلسطينية لتطوير الرياضيين النخبة والمسارات الأولمبية.'
    },
    since: 2017,
    country: 'Palestine',
    active: true
  }
];

/**
 * All Partners Combined
 */
export const partners: Partner[] = [...globalPartners, ...localPartners];

/**
 * Partner Statistics
 */
export const partnerStats = {
  total: partners.length,
  global: globalPartners.length,
  local: localPartners.length,
  featured: partners.filter(p => p.featured).length,
  categories: [...new Set(partners.map(p => p.category).filter(Boolean))].length,
  countries: [...new Set(partners.map(p => p.country).filter(Boolean))].length
};

/**
 * Helper Functions
 */
export const getFeaturedPartners = () => {
  return partners.filter(p => p.featured === true);
};

export const getPartnersByType = (type: PartnerType) => {
  return partners.filter(p => p.type === type);
};

export const getPartnersByCategory = (category: PartnerCategory) => {
  return partners.filter(p => p.category === category);
};

export const getActivePartners = () => {
  return partners.filter(p => p.active !== false);
};

// Export default
export default {
  partners,
  globalPartners,
  localPartners,
  partnerStats,
  getFeaturedPartners,
  getPartnersByType,
  getPartnersByCategory,
  getActivePartners
};
